import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { DEFAULT_PLAYER_LEVEL } from '@/features/onboarding/schemas/add-player-schema';
import type { PendingPlayer } from '@/features/onboarding/stores/onboarding-players-store';
import { type ColorToken, colors, guardianColors } from '@/theme/colors';

/** A golfer rendered as a player card: coach-linked (from the API) or queued locally. */
export type MatchedPlayer = {
  id: string;
  name: string;
  /** Pathway level (1–10); drives the badge + progress colour. */
  level: number;
  /** Progress through the current level, 0–1. */
  progress: number;
  /** Avatar image source; the card falls back to the default user image when absent. */
  avatar?: { uri: string };
  /** A guardian-added player that hasn't been saved yet: shows a "New" badge and
   *  an edit pencil instead of a level badge. */
  isPending: boolean;
};

/** Resolve a level's brand colour (Level 1–10), defaulting to the main blue. */
export const levelColor = (level: number): string =>
  colors[`Level ${level}` as ColorToken] ?? guardianColors['Main/Main'];

/** Progress, 0–1, through the player's current level: earned badges over all badges
 *  across that level. Returns 0 when the level is absent or has no badges to count. */
export const toProgress = (player: GuardianPlayer): number => {
  const current = player.progress?.find(l => l.name === `level${player.level}`);
  let completed = 0;
  let total = 0;
  for (const badge of current?.badges ?? []) {
    total += 1;
    completed += badge.badgeEarned ? 1 : 0;
  }
  return total > 0 ? completed / total : 0;
};

/**
 * Append a cache-busting token to a player's avatar URL so a pull-to-refresh re-fetches
 * the image even when its URL is unchanged (RN Image and the browser both cache by URL, so
 * a plain data refetch leaves a stale avatar on screen). `nonce` of 0 is a no-op — the
 * untouched card — so the first render never carries a cache-bust param.
 */
export const bustAvatarCache = (player: MatchedPlayer, nonce: number): MatchedPlayer => {
  if (!nonce || !player.avatar) return player;
  const { uri } = player.avatar;
  const separator = uri.includes('?') ? '&' : '?';
  return { ...player, avatar: { uri: `${uri}${separator}cb=${nonce}` } };
};

/** Map an API player to the card view model. */
export const toMatchedPlayer = (player: GuardianPlayer): MatchedPlayer => ({
  id: player.id,
  name: [player.firstname, player.lastname].filter(Boolean).join(' ').trim(),
  level: player.level,
  progress: toProgress(player),
  avatar: player.avatarUrl ? { uri: player.avatarUrl } : undefined,
  isPending: false,
});

/** Map a queued (not-yet-saved) player to the card view model. New players are
 *  always level 1 with no recorded progress. */
export const toPendingMatchedPlayer = ({ id, payload }: PendingPlayer): MatchedPlayer => ({
  id,
  name: [payload.firstname, payload.lastname].filter(Boolean).join(' ').trim(),
  level: DEFAULT_PLAYER_LEVEL,
  progress: 0,
  avatar: payload.avatarUrl ? { uri: payload.avatarUrl } : undefined,
  isPending: true,
});
